// Ported from dev-scrips-pm-react src/patients/tabs/personal-details-tab.tsx (PROD-1119 Patients UI port).
/**
 * PersonalDetailsTab — Tab 0 of the patient-edit shell (PROD-725).
 *
 * Mirrors Flutter `personal_details_widget.dart`. Composes:
 *   - `PatientInfoSummary` — read-only metadata strip (MRN / DOB / Language /
 *     Last update / Status). Only renders in edit-mode.
 *   - `PatientPhotoUpload` — circular photo control (base64 → useUploadImage).
 *   - `DemographicsSection` — names, DOB, gender, marital, language*, phone,
 *     email, importance, residency, isAdult toggle.
 *
 * Sections deferred to follow-up commits inside this PR:
 *   - IdentityVerificationSection (ID type/number, issue/expiry, country)
 *   - EducationSection (level + "Other" details)
 *   - OccupationSection (job role + employment)
 *   - GuardianSection (when isAdult=false)
 *   - Language typeahead (currently a static placeholder inside DemographicsSection)
 *
 * Save flow:
 *   - Both create and update POST to `/api/Patients/CreateUpdatePerson`
 *     (Flutter parity — same endpoint, `patientId` set on update). The
 *     hook layer (`useCreatePatient` / `useUpdatePatient`) lives in
 *     `@/patients/services/` post-PROD-770.
 *   - Wire body strips sub-resource arrays (`emergencyContactResponse`,
 *     `healthInsuranceResponse`, `corporateAgreementResponse`) — those
 *     go through their own endpoints (PROD-729 / 730 / 731).
 *   - On create-mode success: navigate to `/patients/edit/<newId>?tab=contact-details`
 *     (Flutter `:475-490` `CreatePatientState → ChangeTabIndexEvent(1)`).
 *   - On edit-mode success: success toast, list query invalidates via the
 *     hook's `onSuccess`.
 *
 * The shell's Save / Next button reads the registered action via
 * `usePatientEditShell()`. Tab body sets it in a `useEffect` so the
 * shell's top bar reflects the current form state (label / disabled / busy).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useWatch, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { serverErrorMessage } from '@/shared/lib/toast-helpers';
import { useAuthStore } from '@/auth/auth-store';
import { usePermission } from '@/auth/permission-gate';
import { SessionOrgPending } from '@/patients/tabs/session-org-pending';
import { useGenders, useIdTypes } from '@/shared/services/lookups';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { Button } from '@scripsteam/scrips-signal-ds';
import { Switch } from '@scripsteam/scrips-signal-ds';
import { usePatientEditShell } from '@/patients/components/patient-edit-shell-context';
import { FormSection, PatientTypeCards } from '@/patients/components/personal-details/form-primitives';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import { PatientInfoSummary } from '@/patients/components/personal-details/patient-info-summary';
import { PatientPhotoUpload } from '@/patients/components/personal-details/patient-photo-upload';
import { IdCardUpload } from '@/patients/components/personal-details/id-card-upload';
import { ContactSection } from '@/patients/components/personal-details/contact-section';
import { DemographicsSection } from '@/patients/components/personal-details/demographics-section';
import { IdentityVerificationSection } from '@/patients/components/personal-details/identity-verification-section';
import { EducationSection } from '@/patients/components/personal-details/education-section';
import { OccupationSection } from '@/patients/components/personal-details/occupation-section';
import { GuardianSection } from '@/patients/components/personal-details/guardian-section';
import {
  personalDetailsSchema,
  defaultFormValues,
  type PersonalDetailsFormValues,
} from '@/patients/components/personal-details/personal-details-schema';
import { useCreatePatientFromMenu } from '@/patients/services/use-create-patient-from-menu';
import { useUpdatePatient } from '@/patients/services/use-update-patient';
import { useEmiratesIdScan } from '@/patients/realtime/use-emirates-id-scan';
import { useEmiratesIdAgentScan } from '@/patients/services/use-emirates-id-agent-scan';
import {
  parseEmiratesIdScan,
  pickScanBranch,
} from '@/patients/realtime/emirates-id-parser';
import type { EmiratesIdCardData } from '@/shared/types/emirates-id';
import type { Patient } from '@/types/patient-api';
import { missingPersonalDetails } from '@/patients/lib/missing-details';
import { MissingFieldsContext } from '@/patients/components/personal-details/missing-fields-context';

const SAVE_FAILED = "Couldn't save patient details";

export interface PersonalDetailsTabProps {
  patient: Patient | null;
  isCreateMode: boolean;
  /**
   * Suppress the full-width `PatientInfoSummary` grey band. Default `false`
   * (standalone patient-edit page keeps it). The appointment-view panel sets
   * this `true` because its own header already shows name / DOB / MRN, so the
   * band would duplicate. Optional + default-off → the patient-edit page is
   * unchanged.
   */
  hideInfoSummary?: boolean;
  /**
   * Fired AFTER a successful edit-mode save. Lets an embedding surface refresh
   * its own data (e.g. the appointment panel invalidates appointment-details
   * so the top strip reflects an edited name / contact). Optional + default
   * undefined → no behaviour change for the patient-edit page.
   */
  onSaved?: () => void;
  /**
   * The appointment page asking the user to complete the patient. Undefined (the default, and the patient chart) =
   * nothing changes. A number turns the missing-fields view on: the empty required fields show their red error state,
   * one line names them, and the page scrolls to the first and focuses it. A new number asks again.
   */
  missingRequest?: number;
}

/**
 * PROD-2959: the organization and practice a create or save is written under come from the signed-in
 * user and nowhere else. Until both are known the tab renders `SessionOrgPending`, never the form —
 * it used to fall back to a hard-coded DEV organization and practice.
 */
export function PersonalDetailsTab(props: PersonalDetailsTabProps) {
  const auth = useAuthStore((s) => s.user);
  const orgId = auth?.organizationId;
  const practiceId = auth?.practiceId;
  if (!orgId || !practiceId) return <SessionOrgPending userLoaded={!!auth} />;
  return <PersonalDetailsTabBody {...props} orgId={orgId} practiceId={practiceId} />;
}

function PersonalDetailsTabBody({
  patient,
  isCreateMode,
  hideInfoSummary = false,
  onSaved,
  missingRequest,
  orgId,
  practiceId,
}: PersonalDetailsTabProps & { orgId: string; practiceId: string }) {
  const navigate = useNavigate();
  const auth = useAuthStore((s) => s.user);
  // Flutter parity (`personal_details_widget.dart:1175`): the External MRN
  // section renders only when the org issues external MRNs.
  const hasExternalMRN = auth?.hasExternalMRN === true;

  // Flutter parity (`:586`): save requires Manage_Patients_Demographics.edit.
  const canEdit = usePermission(['Manage_Patients_Demographics'], { edit: true });

  // Flutter parity (`personal_details_widget.dart:1287`): the contact section
  // is permission-gated on `Patient_Contacts` (view) in edit mode, and always
  // shown in create mode. Its header reads "Guardian's Contact Details" for a
  // minor (the contact belongs to the guardian).
  const canViewContacts = usePermission(['Patient_Contacts'], { view: true });
  const showContact = isCreateMode || canViewContacts;

  const defaults = useMemo<PersonalDetailsFormValues>(
    () => ({ ...defaultFormValues(orgId, practiceId) }),
    [orgId, practiceId],
  );

  const form = useForm<PersonalDetailsFormValues>({
    defaultValues: patient ? patientToFormValues(patient, orgId, practiceId) : defaults,
    resolver: zodResolver(personalDetailsSchema),
    mode: 'onChange',
  });
  const { formState, handleSubmit, reset } = form;
  const { isDirty, isValid, isSubmitting, dirtyFields } = formState;
  // "Has the user changed a field?" Per-field tracking, as Practice does: RHF's aggregate `isDirty` can be true on a
  // cold load while `dirtyFields` is still empty (reset timing).
  const hasChanges = Object.keys(dirtyFields).length > 0;
  // A save succeeded in this visit and nothing has been edited since: the shell shows "Changes saved" instead of Save.
  const [savedOnce, setSavedOnce] = useState(false);
  // The last save's failure, shown as the form action bar's error status until the next attempt (same words as the toast).
  const [saveError, setSaveError] = useState<string | null>(null);
  useEffect(() => {
    if (hasChanges) setSavedOnce(false);
  }, [hasChanges]);

  // Identity Verification + Guardian both report potential duplicates
  // here. Flutter blocks the Save while either side has matches pending
  // (`patient_list_screen.dart:1467` for patient identity;
  // `guardians_identity_verification.dart:75-82` for the guardian side
  // — wired since PROD-777). PROD-726 will own the "use anyway" popup
  // that clears them.
  const [matchingCount, setMatchingCount] = useState(0);
  const [guardianMatchingCount, setGuardianMatchingCount] = useState(0);

  // Missing-fields view (only when the appointment page asks): the live list of what is still empty, the red state on
  // those fields, and the scroll + focus to the first one.
  const askedForMissing = missingRequest !== undefined;
  const watchedValues = useWatch({ control: form.control });
  const missing = useMemo(
    () => (askedForMissing ? missingPersonalDetails(watchedValues as Partial<PersonalDetailsFormValues>) : []),
    [askedForMissing, watchedValues],
  );
  const missingKeys = useMemo(() => new Set(missing.map((m) => m.key)), [missing]);
  const stillFlagged = askedForMissing && watchedValues.isMissingData === true;
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (missingRequest === undefined) return;
    let frame = 0;
    // The schema's own validation draws the red state (and its messages) on the untouched required fields.
    void form.trigger().then(() => {
      frame = requestAnimationFrame(() => {
        const first = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"], [data-missing-field]');
        if (!first) return;
        const target = first.matches('input, textarea, select, button') ? first : first.querySelector<HTMLElement>('button, input');
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
        (target ?? first).focus({ preventScroll: true });
        first.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
      });
    });
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingRequest]);

  // Re-seed the form when the patient finishes loading in edit-mode.
  useEffect(() => {
    if (patient) {
      reset(patientToFormValues(patient, orgId, practiceId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.patientId]);

  // Save mutations — Flutter uses DIFFERENT endpoints for create vs.
  // edit (confirmed 2026-05-14):
  //   - Create (new patient from the patient menu): POST /api/Patients
  //     via `useCreatePatientFromMenu` (Flutter `createPatient()`)
  //   - Edit (existing patient): PUT /api/Patients/{id} via
  //     `useUpdatePatient` (Flutter `updatePatientData()`)
  //
  // `/api/Patients/CreateUpdatePerson` is reserved for the inline
  // create-appointment patient flow (`useCreatePatient`) and is NOT
  // valid here. Earlier PROD-725 shipped with CreateUpdatePerson for
  // both modes — this is the corrective hotfix.
  //
  // SECURITY (review-flagged by Samer on PROD-725 PR #50):
  // The edit path relies entirely on server-side tenant isolation —
  // the backend MUST validate that the authenticated user's
  // organizationId / practiceId own the supplied `patientId` before
  // accepting the PUT. Otherwise a tampered client could overwrite a
  // foreign tenant's record by passing their patientId. The
  // OrganizationId / PracticeId headers injected by the apiClient are
  // the trust anchor here. Not a client-side concern to fix — flagged
  // for backend audit + a Cypress/Playwright e2e test in a follow-up.
  const createMutation = useCreatePatientFromMenu();
  const updateMutation = useUpdatePatient();
  const isSavePending = createMutation.isPending || updateMutation.isPending;

  const onSubmit = handleSubmit(async (values) => {
    try {
      const patientBody = toPatientBody(values, patient);
      if (isCreateMode) {
        const result = await createMutation.mutateAsync({ patient: patientBody });
        const newId = result.patientId;
        if (!newId) {
          toast.error('Patient created but no ID returned.');
          return;
        }
        toast.success(`Successfully added ${values.firstName}`);
        navigate(`/patients/edit/${newId}?tab=contact-details`);
      } else {
        const id = patient?.patientId;
        if (!id) {
          toast.error('Cannot save — patient id missing.');
          return;
        }
        await updateMutation.mutateAsync({ patientId: id, patient: patientBody });
        toast.success('Patient details saved');
        reset(values);
        setSavedOnce(true);
        onSaved?.();
      }
    } catch (err) {
      // No global MutationCache.onError exists in this app: without this the failure was silent.
      toast.error(SAVE_FAILED, { description: serverErrorMessage(err) });
      setSaveError(SAVE_FAILED);
    }
  }, (errors) => {
    // The form was refused before any request: say why (first message), never end silently.
    toast.error(SAVE_FAILED, { description: firstErrorMessage(errors) ?? 'Check the highlighted fields.' });
    setSaveError(SAVE_FAILED);
  });

  // The first schema message for the CURRENT values. It follows the values, not isValid: the tab renders before the
  // patient loads, and a loaded patient still invalid for another field (e.g. no email) keeps isValid false, so a reason
  // worked out only when isValid flips would keep the empty form's "First name is required".
  // Parsed from form.getValues() (every field, as the schema sees them); watchedValues only triggers the recompute.
  const validationReason = useMemo(
    () => personalDetailsSchema.safeParse(form.getValues()).error?.issues[0]?.message ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [watchedValues],
  );

  // Register save action + dirty state with the shell context.
  const { setSaveAction, setIsDirty } = usePatientEditShell();
  useEffect(() => {
    // Save is blocked while either patient-side or guardian-side
    // matching-records lookups return potential duplicates. Guardian
    // gate wired in PROD-777; both flow through `setSaveAction` so the
    // shell's top-bar button reflects the combined state.
    const saveDisabled =
      !canEdit ||
      !isValid ||
      isSubmitting ||
      isSavePending ||
      matchingCount > 0 ||
      guardianMatchingCount > 0;
    // Surface the first blocking reason so the shell's disabled-Save hint
    // isn't a silent dead end (PROD-1718). Ordered by how a user would
    // discover them: permission first (nothing they can fix), then the
    // duplicate-match gates (a visible banner they still need to resolve),
    // then generic "fix the form" for plain validation failures.
    const disabledReason = !canEdit
      ? "You don't have permission to edit patient demographics."
      : matchingCount > 0
        ? 'Resolve the potential duplicate match before saving.'
        : guardianMatchingCount > 0
          ? "Resolve the guardian's potential duplicate match before saving."
          : !isValid
            ? (validationReason ?? 'Fill in the required fields to enable Save.')
            : null;
    setSaveAction({
      label: isCreateMode ? 'Next' : 'Save',
      dirty: hasChanges,
      saved: savedOnce,
      onClick: () => {
        setSaveError(null);
        void onSubmit();
      },
      disabled: saveDisabled,
      busy: isSubmitting || isSavePending,
      disabledReason,
      errorText: saveError,
    });
    return () => setSaveAction(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canEdit,
    isCreateMode,
    isValid,
    isSubmitting,
    isSavePending,
    matchingCount,
    guardianMatchingCount,
    hasChanges,
    savedOnce,
    saveError,
    validationReason,
  ]);

  useEffect(() => {
    setIsDirty(isDirty);
    return () => setIsDirty(false);
  }, [isDirty, setIsDirty]);

  // EmiratesId desk-scanner SignalR auto-fill (PROD-727).
  // Lookup lists are needed to resolve gender/idType labels to GUIDs
  // before we can populate the form. The hub is connected regardless,
  // but a scan that arrives before the lookups load drops that field
  // (parser returns null genderEntry / idTypeEntry) — same as Flutter
  // `.first` would have thrown on an empty list. Safer to no-op.
  const { data: genderList } = useGenders();
  const { data: idTypeList } = useIdTypes();
  const handleScan = useCallback(
    (raw: EmiratesIdCardData) => {
      const scan = parseEmiratesIdScan(raw, {
        genderList: genderList ?? [],
        idTypeList: idTypeList ?? [],
      });
      const opts = { shouldDirty: true, shouldValidate: true } as const;

      // Track whether anything actually applied so we can surface a
      // toast when an arrival was a no-op (scanned card, all fields
      // blank — surprises the user otherwise).
      let appliedCount = 0;
      const apply = <K extends keyof PersonalDetailsFormValues>(
        key: K,
        value: PersonalDetailsFormValues[K],
      ) => {
        // RHF's deep generic for `setValue` resolves
        // `PersonalDetailsFormValues[K]` differently than this caller's
        // K-extending generic — equivalent at runtime, but the variance
        // tripping point isn't worth fixing structurally here. Cast.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form.setValue(key, value as any, opts);
        appliedCount++;
      };

      // Branch routing: scan goes to patient identification only when
      // it AGREES with the form's current isAdult declaration; otherwise
      // it fills the guardian identification. Flutter `utils.dart:1444`.
      // Re-evaluates on every frame (handler captures `form` via ref
      // closure but reads `getValues` fresh), so an isAdult toggle
      // between scans is respected.
      const formIsAdult = form.getValues('isAdult') ?? scan.isAdult;
      const branch = pickScanBranch(scan.isAdult, formIsAdult);

      if (branch === 'patient') {
        if (scan.firstName) apply('firstName', scan.firstName);
        if (scan.middleName !== null) apply('middleName', scan.middleName);
        if (scan.lastName) apply('lastName', scan.lastName);
        if (scan.birthDate) apply('birthDate', scan.birthDate);
        if (scan.genderEntry?.id) {
          apply('gender', scan.genderEntry.id);
          apply('genderName', scan.genderEntry);
        }
        if (scan.idNumber || scan.idTypeEntry || scan.idExpirationDate) {
          apply('identification', {
            ...(form.getValues('identification') ?? {}),
            idNumber: scan.idNumber,
            idType: scan.idTypeEntry?.id ?? null,
            idExpirationDate: scan.idExpirationDate,
          });
        }
      } else {
        if (
          scan.firstName ||
          scan.lastName ||
          scan.birthDate ||
          scan.genderEntry?.id ||
          scan.idNumber
        ) {
          apply('guardianIdenitification', {
            ...(form.getValues('guardianIdenitification') ?? {}),
            firstName: scan.firstName,
            middleName: scan.middleName,
            lastName: scan.lastName,
            birthDate: scan.birthDate,
            gender: scan.genderEntry?.id ?? null,
            idType: scan.idTypeEntry?.id ?? null,
            idNumber: scan.idNumber,
            idExpirationDate: scan.idExpirationDate,
          });
        }
      }

      // Contact fields only auto-populate on create-mode + when the
      // patient hasn't typed them yet (Flutter `:1370` `isBlank(patientId)`).
      if (isCreateMode) {
        if (scan.email && !form.getValues('email')) apply('email', scan.email);
        if (scan.phoneNumber && !form.getValues('phoneNumber')) {
          apply('phoneNumber', scan.phoneNumber);
        }
      }

      // Photo: Flutter stores base64 directly. The React form's
      // photoUrl field accepts a data URL, but the wire shape expects
      // a hosted URL — `PatientPhotoUpload`'s base64 → upload flow
      // handles the conversion when the user later interacts. For
      // now we surface the data URL so the avatar updates immediately.
      if (scan.photoDataUrl && !form.getValues('photoUrl')) {
        apply('photoUrl', scan.photoDataUrl);
      }

      if (appliedCount === 0) {
        // The hub fired but the payload had nothing we could apply —
        // most often a card the scanner couldn't read cleanly. Surface
        // it so the user doesn't think nothing happened.
        toast.warning(
          "EmiratesId scan received but no fields could be applied. Try scanning again.",
        );
      }
    },
    [form, genderList, idTypeList, isCreateMode],
  );
  // PROD-1716 (PA) / PROD-1289 (PM): read via the local headless agent (pull,
  // button-triggered) instead of the desktop-app → SignalR relay (push).
  // Gated behind VITE_EID_USE_AGENT; when on, the SignalR listener is
  // disabled so the form only fills from the agent read (no double-fill).
  const eidUseAgent = import.meta.env.VITE_EID_USE_AGENT === 'true';
  useEmiratesIdScan({
    enabled: canEdit && patient?.isMerged !== true && !eidUseAgent,
    onScan: handleScan,
  });
  const {
    readCard: readEidCard,
    isReading: eidReading,
    error: eidError,
  } = useEmiratesIdAgentScan({ onScan: handleScan });
  useEffect(() => {
    if (!eidError) return;
    const msg =
      eidError.code === 'agent_unreachable'
        ? 'Emirates ID reader agent is not running on this PC.'
        : eidError.code === 'no_reader'
          ? 'No card reader detected.'
          : eidError.code === 'no_card'
            ? 'Insert the Emirates ID card and try again.'
            : eidError.code === 'no_session'
              ? 'Session expired — please sign in again.'
              : 'Could not read the Emirates ID. Try again.';
    toast.error(msg);
  }, [eidError]);

  const photoUrl = form.watch('photoUrl');
  const firstName = form.watch('firstName');
  const lastName = form.watch('lastName');
  const displayName = [firstName, lastName].filter(Boolean).join(' ');
  const isAdult = form.watch('isAdult');
  const isActive = form.watch('isActive');
  const frontIdCardUrl = form.watch('frontIdCardUrl');
  const backIdCardUrl = form.watch('backIdCardUrl');
  // Archived patients can't be toggled active (Flutter
  // `personal_details_widget.dart:494` `onChanged: isArchived ? null : …`).
  const isArchived =
    (patient as { isArchived?: boolean } | null)?.isArchived === true;
  // Flutter gates the WHOLE personal-details form on `AbsorbPointer(absorbing:
  // isMerged)` (`personal_details_widget.dart:448`) — the photo / ID-card taps
  // are NOT permission-gated; only the Save button checks
  // Manage_Patients_Demographics.edit. So the upload controls disable on
  // `isMerged`, not on `!canEdit` (which wrongly killed the controls when the
  // user's edit permission wasn't set).
  const isMerged = patient?.isMerged === true;

  // Switching patient type clears the birth date + resets the guardian,
  // exactly as Flutter does on the MINOR/ADULT card taps
  // (`personal_details_widget.dart:537-543` minor, `:607-613` adult).
  const setPatientType = (nextIsAdult: boolean) => {
    form.setValue('isAdult', nextIsAdult, {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.setValue('birthDate', '', { shouldDirty: true, shouldValidate: true });
    form.setValue(
      'guardianIdenitification',
      nextIsAdult ? null : (form.getValues('guardianIdenitification') ?? {}),
      { shouldDirty: true },
    );
  };

  return (
    <FormProvider {...form}>
     <MissingFieldsContext.Provider value={missingKeys}>
      {/* Full-width patient-info band (grey strip) — Flutter shell header,
          matches the Chart summary header. Sits outside the max-w form.
          Hidden when embedded in a surface that already shows the patient
          header (appointment-view panel). */}
      {hideInfoSummary ? null : <PatientInfoSummary patient={patient} />}
      {/* Single-column form on the page background — Flutter
          `selectedTabWidget(0)` SingleChildScrollView. Section order is a
          1:1 port of `personal_details_widget.dart` build(). */}
      <form ref={formRef} className="max-w-3xl pb-32">
        {missing.length > 0 ? (
          <p role="status" className="sticky top-0 z-10 mb-4 px-8 py-2 bg-surface-background text-sm text-status-error">
            Missing: {missing.map((m) => m.label).join(', ')}
          </p>
        ) : stillFlagged ? (
          <p role="status" className="sticky top-0 z-10 mb-4 px-8 py-2 bg-surface-background text-sm text-text-secondary">
            This patient is still marked incomplete. Press Save to confirm the details.
          </p>
        ) : null}
        {/* PROD-1716: Option 1 pull-read via the local EID agent (flag-gated, default off). */}
        {eidUseAgent ? (
          <div className="mb-4">
            {/* size="default", not the DS "md" this was ported with. Post-reset this file uses
                `@/components/ui/button`, whose size union is default|sm|lg|xs — "md" does not
                exist there. `default` is that kit's standard size. */}
            <Button
              type="button"
              variant="neutral"
              
              onClick={() => void readEidCard()}
              disabled={eidReading || !canEdit || isMerged}
            >
              {eidReading ? 'Reading Emirates ID…' : 'Read Emirates ID'}
            </Button>
          </div>
        ) : null}
        {/* I AM ADDING (create) / PATIENT STATUS + PATIENT IS (edit) */}
        {isCreateMode ? (
          <FormSection
            title="I am adding"
            subtitle="Selecting a patient type will define the future workflow."
            bare
          >
            <PatientTypeCards
              value={isAdult}
              onChange={setPatientType}
              disabled={isMerged}
            />
          </FormSection>
        ) : (
          <>
            <FormSection title="Patient status">
              <div className="flex items-center justify-between">
                <span className="text-size-body-15 text-text-primary">
                  Set patient active
                </span>
                <Switch
                  checked={isActive === true}
                  disabled={isArchived || isMerged}
                  onCheckedChange={(v) =>
                    form.setValue('isActive', v, { shouldDirty: true })
                  }
                  aria-label="Set patient active"
                />
              </div>
              <p className="mt-3 text-size-sm font-normal text-text-secondary">
                Enabling &lsquo;Set patient active&rsquo; will make the
                patient&rsquo;s record active. We recommend to keep the
                patient&rsquo;s record active at all times unless the patient
                hasn&rsquo;t showed up for a long time or is deceased.
              </p>
            </FormSection>
            <FormSection title="Patient is" bare>
              <PatientTypeCards
                value={isAdult}
                onChange={setPatientType}
                disabled={!canEdit}
              />
            </FormSection>
          </>
        )}

        {/* ID CARD — front / back scan tiles. Flutter renders these directly
            under a bare "ID CARD" label (`personal_details_widget.dart:836`). */}
        <FormSection
          title="ID card"
          subtitle="Start with scanning the patient's ID card."
          bare
        >
          <div className="flex flex-wrap gap-4">
            <IdCardUpload
              hideHeaderLabel
              emptyText="Scan front side of ID"
              label="Front"
              url={frontIdCardUrl ?? null}
              disabled={isMerged}
              onChange={(next) =>
                form.setValue('frontIdCardUrl', next, { shouldDirty: true })
              }
            />
            <IdCardUpload
              hideHeaderLabel
              emptyText="Scan back side of ID"
              label="Back"
              url={backIdCardUrl ?? null}
              disabled={isMerged}
              onChange={(next) =>
                form.setValue('backIdCardUrl', next, { shouldDirty: true })
              }
            />
          </div>
        </FormSection>

        {/* External MRN Details — Flutter `:1175` (only when hasExternalMRN). */}
        {hasExternalMRN ? (
          <FormSection title="External MRN details">
            <FieldLabel>External MRN</FieldLabel>
            <TextField
              {...form.register('externalMRN', {
                setValueAs: (v) => (v === '' ? null : v),
              })}
              placeholder="Enter external MRN"
              maxLength={100}
              className="mt-1" size="compact"
            />
          </FormSection>
        ) : null}

        {/* Contact Details / Guardian's Contact Details (header per isAdult). */}
        {showContact ? (
          <FormSection
            title={
              isAdult === false ? "Guardian's contact details" : 'Contact details'
            }
          >
            <ContactSection
              isEdit={!isCreateMode}
              patientId={patient?.patientId ?? null}
              initialEmail={patient?.email ?? ''}
              initialPhone={patient?.phoneNumber ?? ''}
            />
          </FormSection>
        ) : null}

        {/* Personal Details — photo first, then the demographics fields, all
            single-column (Flutter `personal_details_form.dart`). */}
        <FormSection title="Personal details">
          <div className="mb-6">
            <FieldLabel>Patient photo</FieldLabel>
            <div className="mt-2">
              <PatientPhotoUpload
                url={photoUrl}
                onChange={(next) =>
                  form.setValue('photoUrl', next, { shouldDirty: true })
                }
                patientName={displayName}
                disabled={isMerged}
              />
            </div>
          </div>
          <DemographicsSection />
        </FormSection>

        <FormSection title="Identity verification">
          <IdentityVerificationSection
            onMatchingCountChange={setMatchingCount}
            currentPatientId={patient?.patientId ?? null}
            initialIdType={patient?.identification?.idType ?? null}
            initialIdNumber={patient?.identification?.idNumber ?? null}
          />
        </FormSection>

        {/* Occupation is ADULTS-ONLY in Flutter (`if !isMinor`); a minor shows
            the Guardian identity section in its place. Both precede Education. */}
        {isAdult === false ? (
          <FormSection title="Guardian's identity verification">
            <GuardianSection onMatchingCountChange={setGuardianMatchingCount} />
          </FormSection>
        ) : (
          <FormSection title="Occupation">
            <OccupationSection />
          </FormSection>
        )}

        <FormSection title="Education">
          <EducationSection />
        </FormSection>
      </form>
     </MissingFieldsContext.Provider>
    </FormProvider>
  );
}

/** Project a `Patient` (from `usePatient`) onto the form shape. */
// Exported for the chart Demog tab (PROD-1988), which re-hosts these same forms.
export function patientToFormValues(
  patient: Patient,
  orgId: string,
  practiceId: string,
): PersonalDetailsFormValues {
  return {
    organizationId: patient.organizationId ?? orgId,
    practiceId: patient.practiceId ?? practiceId,
    firstName: patient.firstName ?? '',
    middleName: patient.middleName,
    lastName: patient.lastName ?? '',
    birthDate: patient.birthDate ?? '',
    gender: patient.gender ?? '',
    genderName: patient.genderName
      ? { id: patient.genderName.id ?? '', name: patient.genderName.name ?? '' }
      : null,
    email: patient.email,
    phoneNumber: patient.phoneNumber ?? '',
    maritalStatus: patient.maritalStatus,
    maritalStatusName: null,
    language: patient.language,
    languageName: patient.languageName
      ? { id: patient.languageName.id ?? '', name: patient.languageName.name ?? '' }
      : null,
    // The API omits this key for some patients; undefined fails the schema's boolean-or-null and kept the form invalid.
    interpreterRequired: patient.interpreterRequired ?? null,
    isAdult: patient.isAdult ?? true,
    patientMrn: patient.patientMrn,
    externalMRN: patient.externalMRN,
    isActive: patient.isActive,
    isMerged: patient.isMerged,
    isMissingData: patient.isMissingData,
    lastUpdated: patient.lastUpdated,
    photoUrl: patient.photoUrl,
    frontIdCardUrl: patient.frontIdCardUrl ?? null,
    backIdCardUrl: patient.backIdCardUrl ?? null,
    // Importance/Residency wire shape is {code, display, definition} —
    // see patient-api.ts doc-blocks. PROD-725 shipped these as {id, name}
    // which silently dropped existing values on round-trip.
    importance: patient.importance
      ? {
          code: patient.importance.code ?? null,
          display: patient.importance.display ?? null,
          definition: patient.importance.definition ?? null,
        }
      : null,
    residencyType: patient.residencyType
      ? {
          code: patient.residencyType.code ?? null,
          display: patient.residencyType.display ?? null,
          definition: patient.residencyType.definition ?? null,
        }
      : null,
    // Consent / Nationality / Religion — wire shapes verified in
    // patient-api.ts (PatientConsent / NationalityCase / CodeWordCase).
    // Consent falls back to "Yes / Opt in" when the wire ships null,
    // mirroring Flutter `personal_details_form.dart:117-119`.
    consent: patient.consent
      ? {
          code: patient.consent.code ?? null,
          display: patient.consent.display ?? null,
          definition: patient.consent.definition ?? null,
        }
      : { code: '0', display: 'Yes', definition: 'Opt in' },
    nationality: patient.nationality
      ? {
          code: patient.nationality.code ?? null,
          display: patient.nationality.display ?? null,
          system: patient.nationality.system ?? null,
        }
      : null,
    religion: patient.religion
      ? {
          code: patient.religion.code ?? null,
          display: patient.religion.display ?? null,
          system: patient.religion.system ?? null,
          description: patient.religion.description ?? null,
          shortName: patient.religion.shortName ?? null,
        }
      : null,
    // Pass identification + guardian sub-objects through as-is — any
    // server-supplied fields not modelled by the form (e.g. `isReviewed`,
    // legacy phantom fields) survive the round trip. `toPatientBody`
    // merges form-edits on top of the existing snapshot so neither side
    // loses data. Samer review 2026-05-14.
    identification: patient.identification ?? null,
    patientEducation: patient.patientEducation ?? null,
    patientOccupation: patient.patientOccupation ?? null,
    guardianIdenitification: patient.guardianIdenitification
      ? {
          ...patient.guardianIdenitification,
          // Guardian `relationShip` ships PascalCase `{Id, Name, System,
          // Code}` on the wire (PUT-curl-verified 2026-06-04), but the
          // MasterTypeahead reads `{id, name}` — normalise on the way in
          // so the existing relationship actually displays in edit-mode.
          // (Same `/api/Master/Relation` PascalCase quirk as the lookup.)
          relationShip: readRelationShip(
            patient.guardianIdenitification.relationShip,
          ),
        }
      : null,
  };
}

/** Read-side normaliser: accept PascalCase `{Id, Name}` (wire) or lowercase
 *  `{id, name}` and return the `{id, name}` the form/typeahead expects.
 *  Defensive against null / partial guardian records: `null`/non-object →
 *  `null`; only one of Id/Name present → that field set, the other `null`
 *  (so an incomplete legacy record still loads without breaking the form).
 *  Exported for unit testing. */
export function readRelationShip(
  raw: unknown,
): { id: string | null; name: string | null } | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = (r.id ?? r.Id ?? null) as string | null;
  const name = (r.name ?? r.Name ?? null) as string | null;
  if (id == null && name == null) return null;
  return { id, name };
}

/** Write-side: the backend reads guardian `relationShip` as PascalCase
 *  `{Id, Name, System, Code}` (PUT-curl-verified). Convert the form's
 *  `{id, name}` back to that shape so the relationship persists. Returns
 *  `null` for empty/partial-empty input. Exported for unit testing. */
export function toWireRelationShip(
  rel: { id?: string | null; name?: string | null } | null | undefined,
): { Id: string | null; Name: string | null; System: null; Code: null } | null {
  if (!rel || (rel.id == null && rel.name == null)) return null;
  return { Id: rel.id ?? null, Name: rel.name ?? null, System: null, Code: null };
}

/**
 * Project the form back onto a `Patient` for the mutation hook. The
 * hook (`useCreatePatientFromMenu` / `useUpdatePatient`) owns the
 * endpoint-specific serialization (strip-fields, contactId cleanup,
 * etc.) — this tab just hands it the patient object.
 *
 * Carries `existing` patient through for read-only fields the form
 * doesn't edit (consent, age, timeZone, nationality, etc.) so the
 * backend doesn't blank them.
 */
export function toPatientBody(
  values: PersonalDetailsFormValues,
  existing: Patient | null,
): Patient {
  // Cast the merged object to Patient. The form values cover the
  // user-editable subset; existing carries the read-only carry-through
  // fields. The mutation hook strips its own forbidden sub-resources.
  return {
    ...(existing ?? ({} as Patient)),
    patientId: existing?.patientId ?? null,
    organizationId: values.organizationId,
    practiceId: values.practiceId,
    firstName: values.firstName,
    middleName: values.middleName,
    lastName: values.lastName,
    birthDate: values.birthDate,
    gender: values.gender,
    genderName: values.genderName,
    email: values.email,
    phoneNumber: values.phoneNumber,
    maritalStatus: values.maritalStatus,
    language: values.language,
    languageName: values.languageName,
    interpreterRequired: values.interpreterRequired,
    isAdult: values.isAdult,
    patientMrn: values.patientMrn,
    externalMRN: values.externalMRN,
    isActive: values.isActive,
    isMissingData: values.isMissingData ?? false,
    lastUpdated: values.lastUpdated,
    photoUrl: values.photoUrl,
    // ID-card hosted URLs persist on frontIdCardUrl / backIdCardUrl — NOT
    // idFrontImage / idBackImage (those ship empty). Confirmed by the live
    // PUT /api/Patients/{id} body: `"frontIdCardUrl":"https://…png?…",
    // "backIdCardUrl":"", … "idFrontImage":"","idBackImage":""` (2026-06-04).
    frontIdCardUrl: values.frontIdCardUrl,
    backIdCardUrl: values.backIdCardUrl,
    importance: values.importance,
    residencyType: values.residencyType,
    consent: values.consent,
    nationality: values.nationality,
    religion: values.religion,
    // Merge existing sub-objects so any server-supplied fields the form
    // doesn't model (e.g. wire-uncertain `isReviewed`, `idTypeName`,
    // phantom `email` / `contactNumber` on guardian) round-trip
    // unchanged. The form's own values still win on overlapping keys.
    // Samer review 2026-05-14.
    identification: values.identification
      ? { ...(existing?.identification ?? {}), ...values.identification }
      : (existing?.identification ?? null),
    patientEducation: values.patientEducation,
    patientOccupation: values.patientOccupation,
    guardianIdenitification: values.guardianIdenitification
      ? {
          ...(existing?.guardianIdenitification ?? {}),
          ...values.guardianIdenitification,
          // Send PascalCase `relationShip` + the mirrored GUID `relationId`
          // — the backend reads this shape (PUT-curl-verified). Without the
          // conversion the lowercase `{id,name}` is silently dropped.
          //
          // Fall back to the EXISTING relationship when the form value is
          // empty. In the normal edit flow `patientToFormValues` seeds the
          // relationship into the form, so `values` carries it — but a
          // partial guardian patch (or any future caller that doesn't seed
          // it) must not wipe a previously-saved relationship. `existing`
          // holds the raw wire shape; `readRelationShip` normalises either
          // casing to `{id,name}` before re-encoding.
          relationShip: toWireRelationShip(
            values.guardianIdenitification.relationShip ??
              readRelationShip(
                existing?.guardianIdenitification?.relationShip ?? null,
              ),
          ) as any, // eslint-disable-line @typescript-eslint/no-explicit-any
          relationId:
            values.guardianIdenitification.relationId ??
            values.guardianIdenitification.relationShip?.id ??
            existing?.guardianIdenitification?.relationId ??
            readRelationShip(
              existing?.guardianIdenitification?.relationShip ?? null,
            )?.id ??
            null,
        }
      : (existing?.guardianIdenitification ?? null),
    // Contact (email + phone) persists through `updatePatientContactRequest`
    // — the backend reads contact from HERE, NOT the top-level `email` /
    // `phoneNumber`. Verified 2026-06-04: a top-level-only change PUTs 200 but
    // the re-GET shows the old values (the stale contact sub-object wins).
    // Flutter sets both (`patient.email` AND
    // `updatePatientContactRequest.email`/`.primaryContact`,
    // `personal_details_widget.dart` contactDetailsWidget) — mirror it so the
    // edit actually sticks. Only override when a contact request already
    // exists (edit mode); create-mode contact is handled by its own flow.
    updatePatientContactRequest: existing?.updatePatientContactRequest
      ? {
          ...existing.updatePatientContactRequest,
          email: values.email ?? '',
          primaryContact: values.phoneNumber ?? '',
        }
      : (existing?.updatePatientContactRequest ?? null),
    isCreatedFromPatientMenu: existing?.isCreatedFromPatientMenu ?? true,
  } as Patient;
}

/** The first message anywhere in the (nested) form errors: `identification.idType` sits one level down. */
function firstErrorMessage(errors: unknown): string | undefined {
  if (!errors || typeof errors !== 'object') return undefined;
  const node = errors as { message?: unknown } & Record<string, unknown>;
  if (typeof node.message === 'string' && node.message) return node.message;
  for (const [key, value] of Object.entries(node)) {
    if (key === 'ref' || key === 'type') continue;
    const found = firstErrorMessage(value);
    if (found) return found;
  }
  return undefined;
}
